import React, { useEffect, useState } from "react";
import axiosClient from "../../../axios";

const AffiliateApplications = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // NEW STATE
  const [showModal, setShowModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [sharePercentage, setSharePercentage] = useState(10);
  const [price, setPrice] = useState(0);

  useEffect(() => {
    fetchAffiliates();
    fetchPrice();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const response = await axiosClient.get(
        "/api/affiliates/list?status=pending"
      );
      setAffiliates(response.data?.data || response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load affiliates");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrice = async () => {
    try {
      const response = await axiosClient.get("/api/option/pricing");
      if (response.data?.option_value) {
        const data = JSON.parse(response.data.option_value);
        setPrice(parseFloat(data.price) || 0);
      }
    } catch (err) {
      console.error("Failed to fetch price:", err);
    }
  };

  // Calculate shares
  const affiliateShare = ((price * sharePercentage) / 100).toFixed(2);
  const ownerShare = ((price * (100 - sharePercentage)) / 100).toFixed(2);

  // OPEN MODAL
  const openApproveModal = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setSharePercentage(10); // default
    setShowModal(true);
  };

  // SUBMIT APPROVAL
  const approveAffiliate = async () => {
    if (!selectedAffiliate) return;

    try {
      const response = await axiosClient.put(
        `/api/affiliates/${selectedAffiliate.id}`,
        {
          status: "active",
          share_percentage: parseFloat(sharePercentage),
        }
      );

      // Log to see what we're getting
      console.log("Response:", response);
      console.log("Response status:", response.status);
      console.log("Response data:", response.data);

      // Check if the request was successful (status 200-299)
      if (response.status >= 200 && response.status < 300) {
        // Remove the approved affiliate from the pending list
        setAffiliates((prev) =>
          prev.filter((aff) => aff.id !== selectedAffiliate.id)
        );

        setShowModal(false);
        setSelectedAffiliate(null);
        alert("Affiliate approved successfully!");
      }
    } catch (err) {
      console.error("Error object:", err);
      console.error("Error response:", err.response);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to approve affiliate"
      );
    }
  };

  const rejectAffiliate = async (id) => {
    try {
      await axiosClient.put(`/api/affiliates/${id}`, {
        status: "rejected",
      });

      // Remove the rejected affiliate from the pending list
      setAffiliates((prev) => prev.filter((aff) => aff.id !== id));

      // Optional: Show success message
      alert("Affiliate rejected successfully!");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to reject affiliate");
    }
  };
  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container">
      <h2 className="mb-4">Applications</h2>

      {!affiliates.length ? (
        <div className="alert alert-info">
          No pending affiliate applications found
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Email</th>
                <th>Referral Code</th>
                <th>Status</th>
                <th>Bank</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map((aff, idx) => (
                <tr key={aff.id}>
                  <td>{idx + 1}</td>
                  <td>{aff.user?.name || "—"}</td>
                  <td>{aff.user?.email || "—"}</td>
                  <td>{aff.referral_code || "—"}</td>
                  <td>
                    <span className={`badge bg-${getBadgeColor(aff.status)}`}>
                      {aff.status}
                    </span>
                  </td>
                  <td>
                    <strong>{aff.bank_name || "—"}</strong>
                    <div>{aff.banking_name || "—"}</div>
                    <div>{aff.account_number || "—"}</div>
                  </td>
                  <td>
                    {aff.status === "pending" ? (
                      <>
                        <button
                          className="btn btn-success btn-sm me-2"
                          onClick={() => openApproveModal(aff)}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => rejectAffiliate(aff.id)}
                        >
                          Reject
                        </button>
                      </>
                    ) : (
                      <span className="text-muted">No actions</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* APPROVE MODAL */}
      {showModal && (
        <div
          className="modal fade show d-block"
          style={{ background: "#0006" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Set Commission Share</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Affiliate Commission: <strong>{sharePercentage}%</strong>
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max="100"
                    value={sharePercentage}
                    onChange={(e) => setSharePercentage(e.target.value)}
                  />
                </div>

                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="card-title mb-3">Share Breakdown</h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <small className="text-muted">Product Price:</small>
                      </div>
                      <div className="col-6 text-end">
                        <strong>₹{price.toFixed(2)}</strong>
                      </div>
                    </div>
                    <hr className="my-2" />
                    <div className="row mb-2">
                      <div className="col-6">
                        <small>Affiliate Share ({sharePercentage}%):</small>
                      </div>
                      <div className="col-6 text-end">
                        <span className="text-success fw-bold">
                          ₹{affiliateShare}
                        </span>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <small>Owner Share ({100 - sharePercentage}%):</small>
                      </div>
                      <div className="col-6 text-end">
                        <span className="text-primary fw-bold">
                          ₹{ownerShare}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success btn-sm"
                  onClick={approveAffiliate}
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getBadgeColor = (status) => {
  switch (status) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "danger";
    default:
      return "secondary";
  }
};

export default AffiliateApplications;
